import {
  SuperGroupRequest,
  DeviceTypeSettingsRequest,
  Role,
  ApiUserRequest,
  UserRequest,
  GlobalSettingsRequest,
  ApiUserColumnsRequest,
  ReadUsersRequest,
} from '@openapi';
import {
  AdministrationAction,
  ApiUser,
  ArrayWithPagination,
  DeviceType,
  DeviceTypeCertificates,
  SuperGroup,
  User,
} from '@services/types';
import callApi from './callApi';
import {
  getApiUsersApi,
  getDeviceTypesApi,
  getSuperGroupsApi,
  getUsersApi,
  getGlobalSettingsApi,
} from './configuration';

const userEndpoints = {
  createUser: (user: Omit<UserRequest, 'id'>) => {
    return getUsersApi().createUser({
      userRequest: {
        ...user,
        role: user.role || Role.Superadmin,
      },
    });
  },
  updateUser: (id: User['id'], user: UserRequest) => {
    return getUsersApi().updateUser({
      id,
      userRequest: {
        ...user,
        role: user.role || Role.Superadmin,
      },
    });
  },
  lockUser: (userId: User['id']) => {
    return getUsersApi().updateUserIsLocked({
      id: userId,
      body: true,
    });
  },
  deleteUser: (userId: User['id']) => {
    return getUsersApi().deleteUser({
      id: userId,
    });
  },
  unlockUser: (userId: User['id']) => {
    return getUsersApi().updateUserIsLocked({
      id: userId,
      body: false,
    });
  },
  getUserById: (userId: User['id']) => {
    return getUsersApi().readUser({
      id: userId,
    });
  },
  getUsers: (req?: ReadUsersRequest) => {
    return getUsersApi()
      .readUsers(req)
      .then(({ elements, totalElementCount }) => {
        return {
          users: elements as User[],
          totalCount: totalElementCount,
        };
      });
  },
  getColumnsDevice: (userId: User['id']) => {
    return getUsersApi().getListOfDeviceColumns({ id: userId });
  },
  getColumnsRules: (userId: User['id']) => {
    return getUsersApi().getListOfRuleColumns({ id: userId });
  },
  getColumnsStbGroup: (userId: User['id']) => {
    return getUsersApi().getListOfStbgroupColumns({ id: userId });
  },
  updateColumnsDevice: (id: User['id'], arr: ApiUserColumnsRequest) => {
    return getUsersApi().updateListOfDeviceColumns({
      id: id,
      apiUserColumnsRequest: arr,
    });
  },
  updateColumnsRules: (id: User['id'], arr: ApiUserColumnsRequest) => {
    return getUsersApi().updateListOfRuleColumns({
      id: id,
      apiUserColumnsRequest: arr,
    });
  },
  updateColumnsStbGroup: (id: User['id'], arr: ApiUserColumnsRequest) => {
    return getUsersApi().updateListOfStbgroupColumns({
      id: id,
      apiUserColumnsRequest: arr,
    });
  },
};

const apiUserEndpoints = {
  createApiUser: (apiUser: Omit<ApiUserRequest, 'id'>) => {
    return getApiUsersApi().createApiUser({
      apiUserRequest: apiUser,
    });
  },
  updateApiUser: (apiUser: ApiUserRequest & { id: ApiUser['id'] }) => {
    return getApiUsersApi().updateApiUser({
      id: apiUser.id,
      apiUserRequest: apiUser,
    });
  },
  lockApiUser: (apiUserId: ApiUser['id']) => {
    return getApiUsersApi().updateApiUserIsLocked({ id: apiUserId, body: true });
  },
  unlockApiUser: (apiUserId: ApiUser['id']) => {
    return getApiUsersApi().updateApiUserIsLocked({ id: apiUserId, body: false });
  },
  getApiUserById: (apiUserId: ApiUser['id']) => {
    return getApiUsersApi().readApiUser({ id: apiUserId });
  },
  getApiUsers: () => {
    return getApiUsersApi()
      .readApiUsers()
      .then(({ elements }) => elements as ApiUser[]);
  },
};

const superGroupsEndpoints = {
  getSuperGroups: () => {
    return getSuperGroupsApi()
      .readSuperGroups()
      .then(({ elements }) => elements);
  },
  createSuperGroup: (superGroup: SuperGroup) => {
    return getSuperGroupsApi().createSuperGroup({ superGroupRequest: superGroup as SuperGroupRequest });
  },
  updateSuperGroup: (superGroup: SuperGroup) => {
    return getSuperGroupsApi().updateSuperGroup({
      id: superGroup.id,
      superGroupRequest: superGroup as SuperGroupRequest,
    });
  },
  deleteSuperGroup: (superGroupId: SuperGroup['id']) => {
    return getSuperGroupsApi().deleteSuperGroup({
      id: superGroupId,
    });
  },
};

const deviceTypesEndpoints = {
  getDeviceTypes: () => {
    return getDeviceTypesApi()
      .readDeviceTypes()
      .then(({ elements }): DeviceType[] => elements);
  },
  createDeviceType: (deviceType: DeviceType) => {
    return getDeviceTypesApi().createDeviceType({ deviceTypeRequest: deviceType });
  },
  updateDeviceType: (deviceType: DeviceType) => {
    return getDeviceTypesApi().updateDeviceType({ id: deviceType.id, deviceTypeRequest: deviceType });
  },
  getDeviceType: (id: DeviceType['id']) => {
    return getDeviceTypesApi().readDeviceType({ id });
  },
  deleteDeviceType: (id: DeviceType['id']) => {
    return getDeviceTypesApi().deleteDeviceType({ id });
  },
  getDeviceTypeSettings: (id: DeviceType['id']) => {
    return getDeviceTypesApi().readDeviceTypeSettings({ id });
  },
  getDeviceTypeVersions: (id: DeviceType['id']) => {
    return getDeviceTypesApi().readDeviceTypeFirmwares({ id: id });
  },
  updateDeviceTypeSettings: (deviceTypeId: DeviceType['id'], deviceTypeSettingsRequest: DeviceTypeSettingsRequest) => {
    return getDeviceTypesApi().updateDeviceTypeSettings({
      id: deviceTypeId,
      deviceTypeSettingsRequest,
    });
  },
  getDeviceTypeById: (id: DeviceType['id']) => {
    return getDeviceTypesApi()
      .readDeviceType({ id: id })
      .then((elements): DeviceType => elements);
  },

  getDeviceTypeCertificates: (id: DeviceTypeCertificates['id']) => {
    return getDeviceTypesApi()
      .readDeviceTypeCertificates({ deviceTypeId: id })
      .then(({ elements }): DeviceTypeCertificates[] => elements);
  },

  createDeviceTypeCertificates: (deviceCertificate: DeviceTypeCertificates) => {
    return getDeviceTypesApi().createDeviceTypeCertificate({
      deviceTypeId: deviceCertificate.deviceTypeId,
      deviceTypeCertificateRequest: deviceCertificate,
    });
  },

  deleteDeviceTypeCertificate: (
    deviceTypeId: DeviceTypeCertificates['id'],
    deviceTypeCertificateId: DeviceTypeCertificates['id'],
  ) => {
    return getDeviceTypesApi().deleteDeviceTypeCertificate({
      deviceTypeId,
      deviceTypeCertificateId,
    });
  },

  updateDeviceTypeCertificates: (deviceCertificate: DeviceTypeCertificates) => {
    return getDeviceTypesApi().updateDeviceTypeCertificate({
      deviceTypeId: deviceCertificate.deviceTypeId,
      deviceTypeCertificateId: deviceCertificate.id,
      deviceTypeCertificateRequest: deviceCertificate,
    });
  },
  getDeviceTypeRelation: (id: DeviceType['id']) => {
    return getDeviceTypesApi().readDeviceTypeRelationEntities({ id });
  },
};

const globalSettingsEndpoints = {
  updateGlobalSettings: (values: GlobalSettingsRequest) => {
    return getGlobalSettingsApi().updateGlobalSettings({ globalSettingsRequest: values });
  },

  getGlobalSettings: () => {
    return getGlobalSettingsApi().readGlobalSettings();
  },

  getSettings: () => {
    return getGlobalSettingsApi().readAppProps();
  },
};

const actionHistoryEndpoints = {
  getActionHistory: (from: number, to: number) => {
    return callApi.get<ArrayWithPagination<AdministrationAction>>(`/get-action-history?from=${from}&to=${to}`);
  },
};

const administration = {
  ...userEndpoints,
  ...apiUserEndpoints,
  ...superGroupsEndpoints,
  ...deviceTypesEndpoints,
  ...globalSettingsEndpoints,
  ...actionHistoryEndpoints,
};

export default administration;
